import { FeaturePermission } from '../constants/featurePermissions';

export interface RoutePermission {
  path: string;
  features?: FeaturePermission[];
  requireAll?: boolean;
  minimumLevel?: 'read' | 'write' | 'admin';
  customCheck?: (context: any) => boolean;
  fallbackRoles?: string[];
  redirectTo?: string;
}

// Route permission configurations
export const ROUTE_PERMISSIONS: Record<string, RoutePermission> = {
  // Dashboard routes
  '/dashboard': {
    path: '/dashboard',
    customCheck: (context) => context.isAuthenticated,
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager', 'accountant', 'designer', 'maintenance_coordinator', 'field_staff', 'sales_agent', 'customer_support']
  },

  // Property management routes
  '/dashboard/properties': {
    path: '/dashboard/properties',
    features: ['properties_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  '/dashboard/properties/create': {
    path: '/dashboard/properties/create',
    features: ['properties_write'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  // Tenant management routes
  '/dashboard/tenants': {
    path: '/dashboard/tenants',
    features: ['tenants_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  '/dashboard/tenant-management': {
    path: '/dashboard/tenant-management',
    features: ['tenants_write'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  // Booking routes (reservations)
  '/dashboard/bookings': {
    path: '/dashboard/bookings',
    features: [],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager', 'accountant']
  },

  // Payment routes
  '/dashboard/payments': {
    path: '/dashboard/payments',
    features: ['payments_read', 'invoices_read'],
    requireAll: false, // User needs either payments OR invoices access
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager', 'accountant']
  },

  // Maintenance routes
  '/dashboard/maintenance': {
    path: '/dashboard/maintenance',
    features: ['maintenance_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager', 'maintenance_coordinator', 'field_staff']
  },

  // Analytics routes
  '/dashboard/analytics': {
    path: '/dashboard/analytics',
    features: ['analytics_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  // Company management routes
  '/dashboard/companies': {
    path: '/dashboard/companies',
    features: ['company_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner']
  },

  '/dashboard/company-management': {
    path: '/dashboard/company-management',
    features: ['company_write'],
    fallbackRoles: ['super_admin', 'admin', 'owner']
  },

  // User management routes
  '/dashboard/company-users': {
    path: '/dashboard/company-users',
    features: ['users_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  // Admin routes
  '/dashboard/admin-management': {
    path: '/dashboard/admin-management',
    features: ['admin_read'],
    fallbackRoles: ['super_admin']
  },

  // Notification routes
  '/dashboard/notifications': {
    path: '/dashboard/notifications',
    features: ['notifications_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  '/dashboard/notification-logs': {
    path: '/dashboard/notification-logs',
    features: ['notifications_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner']
  },

  // Chat routes
  '/dashboard/chat': {
    path: '/dashboard/chat',
    customCheck: (context) => context.isAuthenticated && context.userRole !== 'tenant',
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager', 'accountant', 'designer', 'maintenance_coordinator', 'field_staff', 'sales_agent', 'customer_support']
  },

  // Profile route
  '/dashboard/profile': {
    path: '/dashboard/profile',
    customCheck: (context) => context.isAuthenticated
  },

  // Virtual tours
  '/dashboard/virtual-tours': {
    path: '/dashboard/virtual-tours',
    features: ['properties_read'], // Virtual tours are part of property management
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager', 'designer']
  },

  // Map demo
  '/dashboard/map-demo': {
    path: '/dashboard/map-demo',
    features: ['properties_read'],
    fallbackRoles: ['super_admin', 'admin', 'owner', 'manager']
  },

  // Test notifications
  '/dashboard/test-notifications': {
    path: '/dashboard/test-notifications',
    features: ['notifications_write'],
    fallbackRoles: ['super_admin', 'admin', 'owner']
  }
};

// Tenant-specific routes
export const TENANT_ROUTE_PERMISSIONS: Record<string, RoutePermission> = {
  '/tenant/dashboard': {
    path: '/tenant/dashboard',
    customCheck: (context) => context.userRole === 'tenant'
  },

  '/tenant/properties': {
    path: '/tenant/properties',
    customCheck: (context) => context.userRole === 'tenant'
  }
};

// Helper functions
export const getRoutePermission = (path: string): RoutePermission | null => {
  // Exact match first
  if (ROUTE_PERMISSIONS[path]) {
    return ROUTE_PERMISSIONS[path];
  }

  if (TENANT_ROUTE_PERMISSIONS[path]) {
    return TENANT_ROUTE_PERMISSIONS[path];
  }

  // Partial match for dynamic routes
  const matchingRoute = Object.keys(ROUTE_PERMISSIONS).find(routePath => {
    if (routePath.includes('*') || routePath.includes(':')) {
      const regex = new RegExp(routePath.replace('*', '.*').replace(/:[^/]+/g, '[^/]+'));
      return regex.test(path);
    }
    return path.startsWith(routePath);
  });

  return matchingRoute ? ROUTE_PERMISSIONS[matchingRoute] : null;
};

export const getAllProtectedRoutes = (): string[] => {
  return [
    ...Object.keys(ROUTE_PERMISSIONS),
    ...Object.keys(TENANT_ROUTE_PERMISSIONS)
  ];
};

export const getRoutesForFeature = (feature: FeaturePermission): string[] => {
  return Object.values(ROUTE_PERMISSIONS)
    .filter(route => route.features?.includes(feature))
    .map(route => route.path);
};

export const getRoutesForRole = (role: string): string[] => {
  return Object.values(ROUTE_PERMISSIONS)
    .filter(route => route.fallbackRoles?.includes(role))
    .map(route => route.path);
};
